import {
  Drawer,
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { ACTIVE_COLOR, DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { landSizes, downPayments, monthlyPayments } from "@/constants/forms";
import createLead from "@/services/deals/create";
import { useEffect } from "react";
import { Deal } from "@/types/lead";
import updateOne from "@/services/deals/update";
import dayjs from "dayjs";
import { useUsers } from "@/hooks/useUsers";
import { get } from "lodash";
import { useProjects } from "@/services/useProjects";
import { useTypes } from "@/services/useTypes";
import "dayjs/locale/es";
import { useStatus } from "@/services/useStatus";

interface DealDrawerProps {
  open: boolean;
  onClose: () => void;
  deal?: Deal | null;
}

type Projects = "Hacienda Vista Juriquilla" | "Novotech SSEISO";

const dealSchema = yup.object({
  name: yup.string().required("Nombre es requerido"),
  value: yup.number().min(1).required("Valor requerido"),
  user: yup.string().optional(),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, "Teléfono inválido")
    .required("Teléfono es requerido"),
  email: yup.string().email("Correo inválido").optional(),
  project: yup.string().required("Proyecto es requerido"),
  landSize: yup.string().required("Medidas del terreno son requeridas"),
  downPayment: yup.string().required("Enganche es requerido"),
  monthlyPayment: yup.string().required("Mensualidad es requerida"),
  appointmentDate: yup.date().nullable().optional(),
  landSizeAdquired: yup.number().optional(),
  agreedDownPayment: yup.number().optional(),
  agreedMonthlyPayment: yup.number().optional(),
  paymentType: yup.string().optional(),
  type: yup.string().required("El tipo de contacto es requerido"),
  status: yup
    .object({
      _id: yup.string().required("El ID del estatus es requerido"),
      name: yup.string().required("El nombre del estatus es requerido"),
    })
    .required("El estatus es requerido"),
});

export const DealDrawer: React.FC<DealDrawerProps> = ({
  open,
  onClose,
  deal,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: users } = useUsers();
  const { data: campaigns } = useProjects();
  const { data: types } = useTypes();
  const { data: statuses } = useStatus();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    watch,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(dealSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      project: "",
      landSize: "",
      downPayment: "",
      monthlyPayment: "",
      appointmentDate: null,
      user: "",
      paymentType: "",
      landSizeAdquired: 0,
      agreedDownPayment: 0,
      agreedMonthlyPayment: 0,
      type: "",
      status: {},
    },
  });

  const handleClose = () => {
    reset({
      name: "",
      phone: "",
      email: "",
      project: "",
      landSize: "",
      downPayment: "",
      monthlyPayment: "",
      appointmentDate: null,
      user: "",
      paymentType: "",
      landSizeAdquired: 0,
      agreedDownPayment: 0,
      agreedMonthlyPayment: 0,
      type: "",
      status: {},
    });
    onClose();
  };

  const onSubmit = async (data: any) => {
    const userSelected = users?.find(
      (user: { name: string; lastName: string }) =>
        `${user.name} ${user.lastName}` === data.user
    );
    if (deal) {
      await updateOne(deal._id, {
        ...data,
        user: userSelected?._id,
      });
    } else {
      await createLead(data);
    }
    handleClose();
  };

  const selectedProject = watch("project");

  useEffect(() => {
    if (!deal) return;

    const currentValues = getValues();

    if (currentValues.name !== deal.fullName) {
      reset({
        ...deal,
        name: deal.fullName,
        phone: deal.phone,
        email: deal.email,
        project: deal.campaign,
        value: deal.value,
        landSize: deal.landMeasurement,
        downPayment: deal.hitch,
        monthlyPayment: deal.mensuality,
        type: deal.type,
        appointmentDate: deal.appointmentDate
          ? dayjs(deal.appointmentDate).toDate()
          : null,
        user: `${get(deal, "user.name")} ${get(deal, "user.lastName")}`,
        status: get(deal, "status"),
      });
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: isMobile ? "80vw" : "30vw",
          maxWidth: isMobile ? "80vw" : "30vw",
        },
      }}
      onClose={handleClose}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: DEFAULT_COLOR }}
              >
                {deal ? "Editar Trato" : "Agregar Trato"}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12 }}
              sx={{
                backgroundColor: ACTIVE_COLOR,
                p: 1,
                borderRadius: 2,
              }}
            >
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={
                      statuses
                        .filter((s) => s.type === "deal")
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) || []
                    }
                    getOptionLabel={(option) => option.name || ""}
                    value={field.value || null}
                    onChange={(_, value) => {
                      field.onChange(value); // guarda todo el objeto status
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Estatus"
                        error={!!errors.status}
                        helperText={errors.status?.message}
                        sx={{
                          "& .MuiInputLabel-root": { color: "white" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "white" },
                            "&:hover fieldset": { borderColor: "white" },
                            "&.Mui-focused fieldset": { borderColor: "white" },
                            "& input": { color: "white" },
                          },
                          "& .MuiSvgIcon-root": { color: "white" },
                          "& .MuiFormHelperText-root": { color: "white" },
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-option": {
                        color: "black",
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nombre"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Teléfono"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Correo Electrónico"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="value"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    fullWidth
                    label="Valor del trato"
                    error={!!errors.value}
                    helperText={errors.value?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="project"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={campaigns.map((c) => c.name) || []}
                    value={field.value}
                    onChange={(_, value) => {
                      reset({
                        ...getValues(),
                        landSize: "",
                        downPayment: "",
                        monthlyPayment: "",
                        ...(value && {
                          project: String(value),
                        }),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Proyecto"
                        error={!!errors.project}
                        helperText={errors.project?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disableClearable
                    options={types.map((c) => c.name) || []}
                    value={field.value}
                    onChange={(_, value) => setValue("type", value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Origen de Contacto"
                        error={!!errors.type}
                        helperText={errors.type?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {deal ? (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="user"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disableClearable
                      value={field.value}
                      options={
                        users?.map((user) => `${user.name} ${user.lastName}`) ||
                        []
                      }
                      onChange={(_, value) => setValue("user", value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Asesor / Cerrador"
                          error={!!errors.user}
                          helperText={errors.user?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            ) : null}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="landSize"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={landSizes[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Superficie propuesta"
                        error={!!errors.landSize}
                        helperText={errors.landSize?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="downPayment"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={downPayments[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Enganche propuesto"
                        error={!!errors.downPayment}
                        helperText={errors.downPayment?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="monthlyPayment"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={monthlyPayments[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Mensualidad propuesta"
                        error={!!errors.monthlyPayment}
                        helperText={errors.monthlyPayment?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="appointmentDate"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    label="Fecha y Hora de Cita"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) =>
                      field.onChange(newValue ? dayjs(newValue).toDate() : null)
                    }
                    format="DD/MM/YYYY HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.appointmentDate,
                        helperText: errors.appointmentDate?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="landSizeAdquired"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    fullWidth
                    label="Superficie del terreno pactado"
                    error={!!errors.landSizeAdquired}
                    helperText={errors.landSizeAdquired?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="agreedDownPayment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    fullWidth
                    label="Enganche pactado"
                    error={!!errors.agreedMonthlyPayment}
                    helperText={errors.agreedMonthlyPayment?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="agreedMonthlyPayment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    fullWidth
                    label="Mensualidad pactada"
                    error={!!errors.agreedMonthlyPayment}
                    helperText={errors.agreedMonthlyPayment?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ p: 1 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Tipo de pago</FormLabel>
                <Controller
                  name="paymentType"
                  control={control}
                  defaultValue="ninguno"
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="enganche"
                        control={<Radio />}
                        label="Enganche"
                        sx={{ width: "100%" }}
                      />
                      <FormControlLabel
                        value="apartado"
                        control={<Radio />}
                        label="Apartado"
                        sx={{ width: "100%" }}
                      />
                      <FormControlLabel
                        value="ninguno"
                        control={<Radio />}
                        label="Ninguno"
                        sx={{ width: "100%" }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ backgroundColor: YELLOW_COLOR }}
              >
                {deal ? "Editar" : "Guardar"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </LocalizationProvider>
    </Drawer>
  );
};
